import { useQuery } from "@tanstack/react-query";
import { voiceApi } from "api/voice";
import { CallRecordingPlayer } from "shared/components/CallRecordingPlayer";
import { Show } from "shared/components/helpers/Show";
import { VoicemailTranscriptText } from "shared/app/Voicemail/VoicemailTranscriptText";
import { voicemailTranscriptText } from "shared/app/Voicemail/voicemail";
import { useInViewOnce } from "shared/hooks/use-in-view-once";
import { useVoicemailPlayed } from "shared/hooks/use-voicemail-played";

// The presigned recording URL lives about an hour; refetch well before that.
const VOICEMAIL_STALE_TIME_MS = 1000 * 60 * 5;

/**
 * CRM-1777: the voicemail a missed call left, on the lead's Activity log —
 * the player and the transcript, from the same endpoint the Inbox uses. A long
 * timeline fetches each voicemail only as its card scrolls near.
 */
export const VoicemailActivityCard = ({
  callId,
  durationSeconds,
  transcriptPreview
}: {
  callId: number;
  durationSeconds?: number | null;
  transcriptPreview?: string | null;
}) => {
  const { ref, inView } = useInViewOnce<HTMLDivElement>("200px");
  const { data, isPending, isError } = useQuery({
    queryKey: ["CALL_VOICEMAIL", callId],
    queryFn: () => voiceApi.getTranscript(callId),
    enabled: inView,
    staleTime: VOICEMAIL_STALE_TIME_MS
  });
  const reportPlayed = useVoicemailPlayed(callId);

  const recordingUrl = data?.recording_url;
  // The preview stands in until the full transcript loads.
  const text = data
    ? voicemailTranscriptText(data.transcript)
    : (transcriptPreview ?? "");

  const recordingFallback = () => {
    if (isPending) return "Loading voicemail...";
    if (isError) return "Couldn't load the voicemail";
    return "Recording unavailable";
  };

  return (
    <div ref={ref} className="max-w-md space-y-2">
      {recordingUrl ? (
        <CallRecordingPlayer
          recordingUrl={recordingUrl}
          durationSeconds={data?.recording_duration_seconds ?? durationSeconds}
          onPlay={reportPlayed}
        />
      ) : (
        <p className="text-xs text-content-200">{recordingFallback()}</p>
      )}
      <Show if={!isPending}>
        <VoicemailTranscriptText text={text} />
      </Show>
    </div>
  );
};
